/**
 * MSK Regions Panel (Phase 3b, 2026-02).
 *
 * Renders zero-to-many MSK region instances inside a pathway module
 * (Clinical Pilates / Exercise Physiology / standalone MSK). Each
 * region is a collapsible card with the 8-section MSK_REGION_SCHEMA
 * rendered via the shared FieldRenderer, drag-and-drop reorder via
 * @dnd-kit, and an "Add musculoskeletal examination" button at the
 * top.
 *
 * Legacy regions (assessments that still hold top-level msk_* data)
 * render as read-only amber banners flagged "needs reconciliation" —
 * Phase-5 will surface a merge tool. Backend already refuses updates
 * and deletes for `regionId === "legacy-msk-migration"`.
 *
 * Backend contract:
 *   GET    /api/assessments/{aid}/msk-regions            → {regions, schema}
 *   POST   /api/assessments/{aid}/msk-regions            → region
 *   PATCH  /api/assessments/{aid}/msk-regions/{rid}      → region
 *   DELETE /api/assessments/{aid}/msk-regions/{rid}      → {ok}
 *   POST   /api/assessments/{aid}/msk-regions/reorder    → {regions}
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Plus, X, Trash2, ChevronDown, ChevronUp, GripVertical, AlertTriangle,
  Loader2, Activity, ArrowRightLeft,
} from "lucide-react";
import { toast } from "sonner";
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor,
  useSensor, useSensors,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { FieldRenderer } from "@/pages/AssessmentEditor";
import MskReconciliationDialog from "@/components/MskReconciliationDialog";

const AUTOSAVE_MS = 800;

function RegionCard({
  region, schema, readOnly, onPatch, onDelete, isOnly, onOpenReconcile,
}) {
  const [open, setOpen] = useState(true);
  const [openSections, setOpenSections] = useState(() => {
    // Open the first section (identity) by default.
    const map = {};
    (schema?.sections || []).forEach((s, i) => { map[s.key] = i === 0; });
    return map;
  });
  const [localResponses, setLocalResponses] = useState(() => region.responses || {});
  const [identity, setIdentity] = useState({
    body_region: region.body_region || "",
    laterality: region.laterality || "",
    body_region_custom: region.body_region_custom || "",
  });
  const debounceRef = useRef(null);
  const pendingRef = useRef({});
  const isLegacy = !!region.legacy;
  const disabled = readOnly || isLegacy;

  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: region.regionId, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.75 : 1,
  };

  // Push identity changes immediately on blur; response fields autosave.
  const saveIdentity = useCallback((next) => {
    if (disabled) return;
    onPatch(region.regionId, next);
  }, [disabled, onPatch, region.regionId]);

  const flushResponses = useCallback(() => {
    const pending = pendingRef.current;
    if (!pending || Object.keys(pending).length === 0) return;
    pendingRef.current = {};
    onPatch(region.regionId, { responses: pending });
  }, [onPatch, region.regionId]);

  const setFieldValue = useCallback((fieldKey, value) => {
    setLocalResponses((prev) => {
      const cur = prev[fieldKey] || {};
      return {
        ...prev,
        [fieldKey]: { ...cur, value, verification: cur.verification || "amended" },
      };
    });
    pendingRef.current = {
      ...pendingRef.current,
      [fieldKey]: { value, verification: "amended" },
    };
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(flushResponses, AUTOSAVE_MS);
  }, [flushResponses]);

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    // Best-effort flush on unmount.
    flushResponses();
  }, [flushResponses]);

  const toggleSection = (skey) => setOpenSections((p) => ({ ...p, [skey]: !p[skey] }));

  const headerTitle = useMemo(() => {
    if (isLegacy) return "Legacy MSK data (pre-migration)";
    const parts = [identity.body_region || "New region"];
    if (identity.laterality && identity.laterality !== "N/A") parts.push(identity.laterality);
    if (identity.body_region === "Other" && identity.body_region_custom) {
      parts.push(`— ${identity.body_region_custom}`);
    }
    return parts.join(" · ");
  }, [identity, isLegacy]);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`rounded-lg border ${isLegacy ? "border-amber-300 bg-amber-50" : "border-zinc-200 bg-white"}`}
      data-testid={`msk-region-card-${region.regionId}`}
    >
      <div className={`flex items-center gap-2 px-3 py-2 ${isLegacy ? "" : "bg-zinc-50"} rounded-t-lg border-b border-zinc-200`}>
        {!disabled && (
          <button
            type="button"
            className="cursor-grab active:cursor-grabbing text-zinc-500 hover:text-zinc-900 focus-visible:ring-2 focus-visible:ring-indigo-500 focus:outline-none rounded"
            {...attributes}
            {...listeners}
            data-testid={`msk-region-drag-handle-${region.regionId}`}
            aria-label="Reorder region"
          >
            <GripVertical className="h-4 w-4" />
          </button>
        )}
        <Activity className={`h-4 w-4 ${isLegacy ? "text-amber-700" : "text-indigo-600"}`} />
        <button
          type="button"
          className="flex-1 text-left text-sm font-semibold text-zinc-900 truncate"
          onClick={() => setOpen((v) => !v)}
          data-testid={`msk-region-toggle-${region.regionId}`}
        >
          {headerTitle}
        </button>
        {isLegacy && (
          <Badge className="bg-amber-200 text-amber-900 border-amber-300" data-testid={`msk-region-legacy-badge-${region.regionId}`}>
            legacy · read-only
          </Badge>
        )}
        {!isLegacy && (
          <Button
            variant="ghost" size="sm"
            className="h-7 w-7 p-0 text-red-700 hover:bg-red-50"
            onClick={() => {
              if (window.confirm(`Remove the ${headerTitle} region? This can't be undone.`)) {
                onDelete(region.regionId);
              }
            }}
            disabled={readOnly}
            data-testid={`msk-region-delete-${region.regionId}`}
            aria-label="Delete region"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
        <button
          type="button"
          className="text-zinc-500 hover:text-zinc-900"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Collapse region" : "Expand region"}
        >
          {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
      </div>

      {open && (
        <div className="p-3 space-y-3">
          {isLegacy && (
            <div className="rounded-md border border-amber-300 bg-amber-100 p-3 text-xs text-amber-900" data-testid={`msk-region-legacy-banner-${region.regionId}`}>
              <div className="font-semibold flex items-center gap-1">
                <AlertTriangle className="h-3.5 w-3.5" />
                Needs reconciliation
              </div>
              <div className="mt-1">
                This region carries pre-migration MSK data. Open the reconciliation tool to move any
                plan text (Impression / Home programme) into the unified Clinical Summary and Plan
                section — the region will unlock for edit and this banner will disappear.
              </div>
              {region.needs_reconciliation && (
                <div className="mt-2 text-2xs">
                  <span className="font-semibold">Legacy plan-text fields still present:</span>{" "}
                  {Object.keys(region.legacy_responses || {}).join(", ") || "—"}
                </div>
              )}
              {!readOnly && (
                <div className="mt-2">
                  <Button
                    size="sm"
                    variant="default"
                    className="bg-amber-900 hover:bg-amber-950 text-white"
                    onClick={onOpenReconcile}
                    data-testid={`msk-region-reconcile-btn-${region.regionId}`}
                  >
                    <ArrowRightLeft className="h-3.5 w-3.5 mr-1" />
                    Open reconciliation…
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Identity row */}
          {!isLegacy && (
            <div className="grid gap-3 md:grid-cols-3">
              <div>
                <Label className="text-xs text-zinc-700">Body region</Label>
                <Select
                  value={identity.body_region || "_none"}
                  onValueChange={(v) => {
                    const next = { ...identity, body_region: v === "_none" ? "" : v };
                    setIdentity(next);
                    saveIdentity({ body_region: next.body_region });
                  }}
                  disabled={disabled}
                >
                  <SelectTrigger data-testid={`msk-region-body-region-${region.regionId}`} className="h-9">
                    <SelectValue placeholder="Body region" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">—</SelectItem>
                    {(schema?.body_regions || BODY_REGIONS_FALLBACK).map((r) => (
                      <SelectItem key={r} value={r}>{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-zinc-700">Side</Label>
                <Select
                  value={identity.laterality || "_none"}
                  onValueChange={(v) => {
                    const next = { ...identity, laterality: v === "_none" ? "" : v };
                    setIdentity(next);
                    saveIdentity({ laterality: next.laterality });
                  }}
                  disabled={disabled}
                >
                  <SelectTrigger data-testid={`msk-region-laterality-${region.regionId}`} className="h-9">
                    <SelectValue placeholder="Side" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">—</SelectItem>
                    {LATERALITY_FALLBACK.map((l) => (
                      <SelectItem key={l} value={l}>{l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {identity.body_region === "Other" && (
                <div>
                  <Label className="text-xs text-zinc-700">Custom region</Label>
                  <Input
                    value={identity.body_region_custom}
                    onChange={(e) => setIdentity({ ...identity, body_region_custom: e.target.value })}
                    onBlur={() => saveIdentity({ body_region_custom: identity.body_region_custom })}
                    disabled={disabled}
                    className="h-9"
                    data-testid={`msk-region-custom-${region.regionId}`}
                  />
                </div>
              )}
            </div>
          )}

          {/* Section-by-section fields */}
          {(schema?.sections || []).map((section) => {
            // Skip the identity section — rendered above with the
            // dedicated body-region / laterality controls.
            if (section.key === "region_identity") {
              const nonIdentityFields = (section.fields || []).filter(
                (f) => !["body_region", "laterality", "body_region_custom"].includes(f.key),
              );
              if (nonIdentityFields.length === 0) return null;
              const skey = section.key;
              return (
                <div key={skey} className="rounded border border-zinc-200">
                  <button
                    type="button"
                    onClick={() => toggleSection(skey)}
                    className="w-full text-left px-3 py-2 flex items-center justify-between bg-zinc-50 hover:bg-zinc-100 rounded-t"
                    data-testid={`msk-region-section-toggle-${region.regionId}-${skey}`}
                  >
                    <span className="font-medium text-zinc-900 text-sm">More region context</span>
                    {openSections[skey] ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </button>
                  {openSections[skey] && (
                    <div className="p-3 space-y-3">
                      {nonIdentityFields.map((field) => (
                        <FieldBlock
                          key={field.key}
                          field={field}
                          resp={localResponses[field.key]}
                          onChange={setFieldValue}
                          readOnly={disabled}
                          testIdPrefix={`msk-region-field-${region.regionId}`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            return (
              <div key={section.key} className="rounded border border-zinc-200">
                <button
                  type="button"
                  onClick={() => toggleSection(section.key)}
                  className="w-full text-left px-3 py-2 flex items-center justify-between bg-zinc-50 hover:bg-zinc-100 rounded-t"
                  data-testid={`msk-region-section-toggle-${region.regionId}-${section.key}`}
                >
                  <span className="font-medium text-zinc-900 text-sm">{section.label}</span>
                  {openSections[section.key] ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </button>
                {openSections[section.key] && (
                  <div className="p-3 space-y-3">
                    {(section.fields || []).map((field) => (
                      <FieldBlock
                        key={field.key}
                        field={field}
                        resp={localResponses[field.key]}
                        onChange={setFieldValue}
                        readOnly={disabled}
                        testIdPrefix={`msk-region-field-${region.regionId}`}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function FieldBlock({ field, resp, onChange, readOnly, testIdPrefix }) {
  return (
    <div className="grid gap-1.5" data-testid={`${testIdPrefix}-${field.key}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <Label className="text-sm">
          {field.label}
          {field.required && <span className="text-red-700 ml-0.5">*</span>}
          {field.safetyCritical && (
            <span className="ml-1 text-2xs uppercase tracking-wider px-1 py-0.5 rounded border border-rose-200 bg-rose-50 text-rose-900">
              safety-critical
            </span>
          )}
        </Label>
      </div>
      {field.help && <div className="text-xs text-zinc-500">{field.help}</div>}
      <FieldRenderer field={field} resp={resp} onChange={onChange} readOnly={readOnly} />
    </div>
  );
}

// Fallbacks in case the schema payload doesn't inline these — the
// backend does return the schema, but this keeps rendering safe.
const BODY_REGIONS_FALLBACK = [
  "Head/face", "Cervical spine", "Thoracic spine", "Lumbar spine",
  "Sacroiliac / Pelvic girdle", "Shoulder", "Elbow", "Wrist / Hand",
  "Hip", "Knee", "Ankle / Foot", "Rib cage / Chest wall", "Other",
];
const LATERALITY_FALLBACK = ["Left", "Right", "Bilateral", "Central / midline", "N/A"];

export default function MskRegionsPanel({ assessmentId, readOnly = false }) {
  const [regions, setRegions] = useState([]);
  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showReconcile, setShowReconcile] = useState(false);
  const [addPayload, setAddPayload] = useState({ body_region: "", laterality: "", body_region_custom: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!assessmentId) return;
    setLoading(true);
    try {
      const r = await api.get(`/assessments/${assessmentId}/msk-regions`);
      setRegions(r.data?.regions || []);
      // The backend returns MSK_REGION_SCHEMA — but doesn't inline
      // BODY_REGIONS / LATERALITY_OPTIONS; extract them from the
      // identity section for the add-dialog + identity dropdowns.
      const sch = r.data?.schema || null;
      if (sch) {
        const identity = (sch.sections || []).find((s) => s.key === "region_identity");
        const bodyField = identity?.fields?.find((f) => f.key === "body_region");
        const latField = identity?.fields?.find((f) => f.key === "laterality");
        sch.body_regions = bodyField?.options || BODY_REGIONS_FALLBACK;
        sch.laterality_options = latField?.options || LATERALITY_FALLBACK;
      }
      setSchema(sch);
    } catch (e) {
      toast.error("Couldn't load MSK regions", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setLoading(false);
    }
  }, [assessmentId]);

  useEffect(() => { load(); }, [load]);

  const patchRegion = useCallback(async (regionId, payload) => {
    try {
      const r = await api.patch(`/assessments/${assessmentId}/msk-regions/${regionId}`, payload);
      const updated = r.data;
      setRegions((prev) => prev.map((x) => (x.regionId === regionId ? { ...x, ...updated } : x)));
    } catch (e) {
      toast.error("Couldn't save region", {
        description: e?.response?.data?.detail || e.message,
      });
    }
  }, [assessmentId]);

  const deleteRegion = useCallback(async (regionId) => {
    try {
      await api.delete(`/assessments/${assessmentId}/msk-regions/${regionId}`);
      setRegions((prev) => prev.filter((x) => x.regionId !== regionId));
      toast.success("Region removed");
    } catch (e) {
      toast.error("Couldn't remove region", {
        description: e?.response?.data?.detail || e.message,
      });
    }
  }, [assessmentId]);

  const createRegion = useCallback(async () => {
    if (!addPayload.body_region || !addPayload.laterality) {
      toast.error("Choose body region and side first");
      return;
    }
    if (addPayload.body_region === "Other" && !addPayload.body_region_custom.trim()) {
      toast.error("Custom region name is required when body region is 'Other'");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post(`/assessments/${assessmentId}/msk-regions`, addPayload);
      setRegions((prev) => [...prev, r.data]);
      setShowAddDialog(false);
      setAddPayload({ body_region: "", laterality: "", body_region_custom: "" });
      toast.success("Region added");
    } catch (e) {
      toast.error("Couldn't add region", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setSaving(false);
    }
  }, [addPayload, assessmentId]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = useCallback(async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    // Legacy region can't be reordered.
    if (active.id === "legacy-msk-migration" || over.id === "legacy-msk-migration") return;
    const oldIdx = regions.findIndex((r) => r.regionId === active.id);
    const newIdx = regions.findIndex((r) => r.regionId === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const reordered = arrayMove(regions, oldIdx, newIdx);
    setRegions(reordered);
    try {
      const orderedIds = reordered
        .filter((r) => !r.legacy)
        .map((r) => r.regionId);
      const r = await api.post(`/assessments/${assessmentId}/msk-regions/reorder`, {
        ordered_ids: orderedIds,
      });
      // Keep the server's canonical ordering (legacy still first if present).
      if (r.data?.regions) setRegions(r.data.regions);
    } catch (e) {
      toast.error("Couldn't save new order", {
        description: e?.response?.data?.detail || e.message,
      });
      load();
    }
  }, [assessmentId, regions, load]);

  const nonLegacyIds = useMemo(
    () => regions.filter((r) => !r.legacy).map((r) => r.regionId),
    [regions],
  );

  return (
    <div className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-3 space-y-3" data-testid="msk-regions-panel">
      <div className="flex items-center gap-2 flex-wrap">
        <Activity className="h-4 w-4 text-indigo-700" />
        <h3 className="font-semibold text-sm text-zinc-900">Musculoskeletal examinations</h3>
        <Badge variant="outline" className="text-2xs" data-testid="msk-regions-count">
          {regions.filter((r) => !r.legacy).length} region{regions.filter((r) => !r.legacy).length === 1 ? "" : "s"}
        </Badge>
        <div className="ml-auto">
          <Button
            size="sm"
            variant="default"
            onClick={() => setShowAddDialog(true)}
            disabled={readOnly}
            data-testid="msk-region-add-btn"
          >
            <Plus className="h-3.5 w-3.5 mr-1" /> Add musculoskeletal examination
          </Button>
        </div>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-xs text-zinc-500">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading regions…
        </div>
      )}

      {!loading && regions.length === 0 && (
        <div className="text-xs text-zinc-600 italic" data-testid="msk-regions-empty">
          No musculoskeletal examinations recorded on this assessment yet.
          Add one above if a specific body region needs examination — the
          appointment-level plan still lives on Clinical Summary and Plan.
        </div>
      )}

      {!loading && regions.length > 0 && schema && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={nonLegacyIds} strategy={verticalListSortingStrategy}>
            <div className="space-y-3">
              {regions.map((region) => (
                <RegionCard
                  key={region.regionId}
                  region={region}
                  schema={schema}
                  readOnly={readOnly}
                  onPatch={patchRegion}
                  onDelete={deleteRegion}
                  isOnly={regions.length === 1}
                  onOpenReconcile={() => setShowReconcile(true)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Add-region dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent data-testid="msk-region-add-dialog">
          <DialogHeader>
            <DialogTitle>Add musculoskeletal examination</DialogTitle>
            <DialogDescription>
              Pick the body region and side. You can flesh out mechanism, pain
              characteristics, ROM, strength and functional tests once the region
              is created — each region has its own set of responses.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-sm">Body region</Label>
              <Select
                value={addPayload.body_region || "_none"}
                onValueChange={(v) => setAddPayload((p) => ({ ...p, body_region: v === "_none" ? "" : v }))}
              >
                <SelectTrigger data-testid="msk-region-add-body-region">
                  <SelectValue placeholder="Choose body region" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">— none —</SelectItem>
                  {(schema?.body_regions || BODY_REGIONS_FALLBACK).map((r) => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm">Side</Label>
              <Select
                value={addPayload.laterality || "_none"}
                onValueChange={(v) => setAddPayload((p) => ({ ...p, laterality: v === "_none" ? "" : v }))}
              >
                <SelectTrigger data-testid="msk-region-add-laterality">
                  <SelectValue placeholder="Choose side" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">— none —</SelectItem>
                  {(schema?.laterality_options || LATERALITY_FALLBACK).map((l) => (
                    <SelectItem key={l} value={l}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {addPayload.body_region === "Other" && (
              <div>
                <Label className="text-sm">Custom region name</Label>
                <Input
                  value={addPayload.body_region_custom}
                  onChange={(e) => setAddPayload((p) => ({ ...p, body_region_custom: e.target.value }))}
                  data-testid="msk-region-add-custom"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={createRegion} disabled={saving} data-testid="msk-region-add-submit">
              {saving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Plus className="h-3.5 w-3.5 mr-1" />}
              Add region
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 5 — Legacy MSK reconciliation dialog */}
      <MskReconciliationDialog
        open={showReconcile}
        onOpenChange={setShowReconcile}
        assessmentId={assessmentId}
        onReconciled={() => load()}
      />
    </div>
  );
}
